import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { PlatformProvider } from './context/PlatformContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import DynamicThemeProvider from './components/ThemeProvider/DynamicThemeProvider';
import PlatformInitializer from './components/PlatformInitializer';
import ErrorBoundary from './components/ErrorBoundary';
import HealthCheck from './components/landing/HealthCheck.jsx';
import RouteConfig from './components/AppComponents/RouteConfig';
import './App.css';
import { debugLog } from './utils/debugLog';

// Main app component with routing
const AppContent = () => {
  const [navigationOpen, setNavigationOpen] = useState(true);

  // Initialize API cleanup on mount
  useEffect(() => {
    // No global cleanup - let individual components handle their own cleanup
    debugLog('🚀 App: Initialized without global API cleanup');

    // Only cleanup on actual page unload (not navigation)
    const handleBeforeUnload = () => {
      // eslint-disable-next-line no-console
      debugLog('🚫 App: Page unloading');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // No cleanup on component unmount - let individual components handle it
    };
  }, []);

  // Check if this is a health check request
  const isHealthCheck = window.location.pathname === '/health';
  if (isHealthCheck) {
    return <HealthCheck />;
  }

  return (
    <Router>
      <RouteConfig
        navigationOpen={navigationOpen}
        onToggleNavigation={() => setNavigationOpen(!navigationOpen)}
      />
    </Router>
  );
};

// Main App component with providers
function App() {
  // Removed tenant redirect check - ProtectedRoute handles everything
  // This prevents redirect loops when tenant data is loading

  return (
    <ErrorBoundary>
      <AuthProvider>
        <FeatureFlagProvider>
          <PlatformProvider>
            <PlatformInitializer>
              <DynamicThemeProvider>
                {/* TemplatesDataProvider removed - all components now use MV endpoints */}
                <CssBaseline />
                <AppContent />
              </DynamicThemeProvider>
            </PlatformInitializer>
          </PlatformProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
